import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { 
  Scale, 
  FileCheck, 
  Calculator, 
  Building2, 
  RefreshCw, 
  Landmark, 
  FileText, 
  Shield, 
  Home,
  ArrowRight 
} from "lucide-react";
import { Link } from "react-router-dom";

const services = [
  {
    icon: FileCheck,
    title: "Isenção de Imposto de Renda",
    description: "Aposentados e pensionistas com doenças graves têm direito à isenção de IR e restituição dos últimos 5 anos.",
    link: "/servicos/isencao-imposto-renda",
  },
  {
    icon: Calculator,
    title: "Revisão Tributária",
    description: "Análise completa dos tributos pagos para identificar valores pagos a mais e recuperar créditos.",
    link: "/servicos/revisao-imposto-renda",
  },
  {
    icon: Landmark,
    title: "Revisão de IPTU, ITBI e ISS",
    description: "Contestação de lançamentos municipais incorretos e recuperação de valores pagos indevidamente.",
    link: "/servicos/revisao-itbi-iptu-iss",
  },
  {
    icon: Building2,
    title: "Planejamento Tributário",
    description: "Prepare sua empresa para a Reforma Tributária de 2026. Estratégias legais para reduzir a carga fiscal.",
    link: "/tributario",
  },
  {
    icon: RefreshCw,
    title: "Recuperação de Créditos",
    description: "Identificação e recuperação de créditos tributários não aproveitados, incluindo PIS/COFINS e ICMS.",
    link: "/tributario",
  },
  {
    icon: Shield,
    title: "Defesa Fiscal",
    description: "Defesa técnica em processos de execução fiscal e contestação de multas tributárias.",
    link: "/tributario",
  },
];

export const PracticeAreas = () => {
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { once: true, margin: "-100px" });

  return (
    <section id="areas" className="py-12 lg:py-16 bg-secondary/30" ref={containerRef}>
      <div className="container mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="mb-12 lg:mb-16 text-center"
        >
          <span className="text-primary text-sm font-medium tracking-widest uppercase mb-4 block">
            Especialistas em Direito Tributário
          </span>
          <h2 className="font-display text-4xl lg:text-6xl font-light text-foreground mb-4">
            Áreas de <span className="text-primary">Atuação</span>
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Atuação técnica para garantir que você pague somente o devido, com total respaldo legal.
          </p>
        </motion.div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, index) => (
            <motion.div
              key={service.title}
              initial={{ opacity: 0, y: 40 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: index * 0.1, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            >
              <Link 
                to={service.link}
                className="group block h-full"
              >
                <div className="relative bg-card border border-border rounded-lg p-6 hover:border-primary/50 transition-all duration-500 card-hover overflow-hidden h-full flex flex-col">
                  {/* Icon */}
                  <div className="flex items-start justify-between mb-4">
                    <motion.div
                      whileHover={{ rotate: 10, scale: 1.1 }}
                      transition={{ duration: 0.3 }}
                      className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center"
                    >
                      <service.icon className="w-6 h-6 text-primary" />
                    </motion.div>
                  </div>

                  {/* Content */}
                  <h3 className="font-display text-lg lg:text-xl text-foreground mb-3 group-hover:text-primary transition-colors duration-300">
                    {service.title}
                  </h3>

                  <p className="text-muted-foreground text-sm leading-relaxed mb-4 flex-1">
                    {service.description}
                  </p>

                  {/* Link */}
                  <div className="flex items-center gap-1 text-primary text-sm font-medium group-hover:gap-2 transition-all">
                    Saiba Mais
                    <ArrowRight className="w-4 h-4" />
                  </div>

                  {/* Hover Line */}
                  <div className="absolute bottom-0 left-0 w-0 h-1 bg-primary group-hover:w-full transition-all duration-500" />
                </div>
              </Link>
            </motion.div>
          ))}
        </div>

        {/* CTA to Pillar Page */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.6, duration: 0.6 }}
          className="text-center mt-12"
        >
          <Link
            to="/tributario"
            className="inline-flex items-center gap-2 px-6 py-3 border border-primary text-primary font-medium rounded-lg hover:bg-primary hover:text-primary-foreground transition-colors"
          >
            Ver todos os serviços tributários
            <ArrowRight className="w-4 h-4" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
};
